// ./src/components/generic/RecurrenceDaysSelector/RecurrenceDaysSelector.tsx
import React from "react";
import { MultiSelect, MultiSelectProps } from "@mantine/core";

const weekdayOptions = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "7", label: "Sunday" },
];

const monthDayOptions = Array.from({ length: 31 }, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString(),
}));

export interface RecurrenceDaysSelectorProps
  extends Omit<
    MultiSelectProps,
    "data" | "value" | "onChange" | "label" | "placeholder"
  > {
  /** The recurrence pattern that determines which options to show */
  recurrencePattern: string | null | undefined;

  /** Current selected days (comma-separated string or array) */
  value: string | string[] | null | undefined;

  /** Callback when selection changes */
  onChange: (value: string[]) => void;

  /** Custom label for the input */
  label?: string;

  /** Custom placeholder for the input */
  placeholder?: string;
}

export const RecurrenceDaysSelector: React.FC<RecurrenceDaysSelectorProps> = ({
  recurrencePattern,
  value,
  onChange,
  label,
  placeholder,
  ...multiSelectProps
}) => {
  const recurrencePatternUpper = recurrencePattern?.toUpperCase() ?? "";
  // Determine if we should show this component
  const shouldShow = ["WEEKLY", "MONTHLY"].includes(recurrencePatternUpper);

  if (!shouldShow) {
    return null;
  }

  // Convert value to array format
  const arrayValue: string[] = React.useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return value.split(",").filter(Boolean);
  }, [value]);

  // Determine which options to use based on recurrence pattern
  const options =
    recurrencePatternUpper === "WEEKLY" ? weekdayOptions : monthDayOptions;

  // Determine labels and settings
  const finalLabel =
    label ||
    (recurrencePatternUpper === "WEEKLY" ? "Days of Week" : "Days of Month");
  const finalPlaceholder =
    placeholder ||
    (recurrencePatternUpper === "WEEKLY"
      ? "Select weekdays"
      : "Select month days");

  // Handle change
  const handleChange = (newValue: string[]) => {
    onChange(newValue);
  };

  return (
    <MultiSelect
      {...multiSelectProps}
      label={finalLabel}
      placeholder={finalPlaceholder}
      value={arrayValue}
      onChange={handleChange}
      data={options}
      required
      withAsterisk
      searchable={recurrencePatternUpper === "MONTHLY"}
      maxValues={recurrencePatternUpper === "WEEKLY" ? 7 : 31}
      size="md"
    />
  );
};

// Export options for use in other components if needed
export { weekdayOptions, monthDayOptions };
